import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import InteractiveTerminal from "../components/InteractiveTerminal";
import AIChatbot from "../components/AIChatbot";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Play,
  Pause,
  PlayCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  Terminal,
  MessageSquare,
  BookOpen,
  Award,
  Clock,
  Send,
  Loader2,
  Lock,
  Menu,
  X,
} from "lucide-react";

const LessonPlayer = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("video");
  const [progress, setProgress] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [collapsedModules, setCollapsedModules] = useState({});

  useEffect(() => {
    setActiveTab("video");
    setProgress(0);
    fetchLesson();
  }, [lessonId]);

  // Charge la structure du cours (pour le sommaire) dès qu'on connaît le course_id
  useEffect(() => {
    if (lesson?.course_id) fetchCourse(lesson.course_id);
  }, [lesson?.course_id]);

  const fetchLesson = async () => {
    try {
      const response = await axios.get(`${API}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLesson(response.data);
      if (response.data.quiz) {
        setQuizAnswers(new Array(response.data.quiz.questions.length).fill(null));
      }
    } catch (error) {
      console.error("Error fetching lesson:", error);
      toast.error("Impossible de charger la leçon");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourse = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCourse(response.data);
    } catch (error) {
      // Sommaire indisponible : on garde le lecteur fonctionnel
    }
  };

  const saveCompletion = async () => {
    if (!lesson) return;
    try {
      await axios.post(
        `${API}/progress/update`,
        {
          course_id: lesson.course_id,
          lesson_id: lessonId,
          completed: true,
          progress_percent: 100,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Silently fail
    }
  };

  // Pour les vidéos MP4 natives : suit la progression et marque terminé au-delà de 90%
  const handleVideoTimeUpdate = (e) => {
    const el = e.target;
    if (!el.duration) return;
    const progressPercent = (el.currentTime / el.duration) * 100;
    setProgress(progressPercent);
    if (progressPercent > 90) saveCompletion();
  };

  // Bouton "Marquer comme terminé" (utile pour YouTube/Vimeo)
  const markComplete = async () => {
    setProgress(100);
    await saveCompletion();
    toast.success("Leçon marquée comme terminée");
  };

  // Extraction des identifiants de vidéo
  const getYouTubeId = (url) => {
    const m = (url || "").match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/
    );
    return m ? m[1] : null;
  };
  const getVimeoId = (url) => {
    const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
  };
  const getDriveId = (url) => {
    const u = url || "";
    const m =
      u.match(/drive\.google\.com\/file\/d\/([\w-]+)/) ||
      u.match(/drive\.google\.com\/open\?id=([\w-]+)/) ||
      u.match(/[?&]id=([\w-]+)/);
    return u.includes("drive.google.com") && m ? m[1] : null;
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);

    try {
      const response = await axios.post(
        `${API}/comments`,
        {
          content: newComment,
          lesson_id: lessonId,
          course_id: lesson.course_id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLesson({
        ...lesson,
        comments: [response.data, ...(lesson.comments || [])],
      });
      setNewComment("");
      toast.success("Commentaire ajouté");
    } catch (error) {
      toast.error("Erreur lors de l'ajout du commentaire");
    } finally {
      setSubmittingComment(false);
    }
  };

  const submitQuiz = async () => {
    if (quizAnswers.some((a) => a === null)) {
      toast.error("Veuillez répondre à toutes les questions");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/quizzes/${lesson.quiz.quiz_id}/submit`,
        quizAnswers,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizResult(response.data);
      toast.success(response.data.passed ? "Quiz réussi! 🎉" : "Quiz terminé");
    } catch (error) {
      toast.error("Erreur lors de la soumission du quiz");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!lesson) return null;

  // Sommaire du cours (façon KodeKloud)
  const isPrivileged = user && (user.role === "admin" || user.role === "instructor");
  const hasAccess = course?.is_enrolled || isPrivileged;
  const lessons = course?.lessons || [];
  const currentIndex = lessons.findIndex((l) => l.lesson_id === lessonId);
  const totalLessons = lessons.length;
  const completedCount = currentIndex >= 0 ? currentIndex : 0;
  const percentComplete = totalLessons ? Math.round((completedCount / totalLessons) * 100) : 0;

  const fmtDuration = (min) => {
    const m = Number(min) || 0;
    return `${String(Math.floor(m)).padStart(2, "0")}:00`;
  };

  const openLesson = (l) => {
    if (l.is_free || hasAccess) {
      navigate(`/lesson/${l.lesson_id}`);
    } else {
      toast.error("Achetez le cours pour accéder à cette leçon");
    }
  };

  // Regroupement des leçons par module (en conservant l'ordre global)
  const buildModules = () => {
    const groups = [];
    const byTitle = {};
    lessons.forEach((l, globalIndex) => {
      const title = l.module?.trim() || "Contenu du cours";
      if (!byTitle[title]) {
        byTitle[title] = { title, lessons: [] };
        groups.push(byTitle[title]);
      }
      byTitle[title].lessons.push({ ...l, globalIndex });
    });
    return groups;
  };
  const moduleGroups = buildModules();
  const toggleModule = (title) =>
    setCollapsedModules((prev) => ({ ...prev, [title]: !prev[title] }));

  return (
    <div className="h-screen overflow-hidden bg-background flex flex-col" data-testid="lesson-player">
      {/* Header */}
      <header className="h-14 border-b border-border bg-card/95 backdrop-blur flex items-center px-4 gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title="Afficher/masquer le sommaire"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading font-bold truncate">{lesson.title}</h1>
          {course && (
            <p className="text-xs text-muted-foreground truncate">{course.title}</p>
          )}
        </div>
        <Link to={`/courses/${lesson.course_id}`}>
          <Button variant="outline" size="sm">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Détails du cours
          </Button>
        </Link>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Sommaire du cours (gauche, style KodeKloud) */}
        {sidebarOpen && (
          <aside className="w-80 shrink-0 border-r border-border bg-card hidden lg:flex flex-col overflow-hidden">
            {/* En-tête cours + progression */}
            <div className="p-4 border-b border-border">
              <Link
                to={`/courses/${lesson.course_id}`}
                className="text-xs text-primary hover:underline flex items-center gap-1 mb-3"
              >
                <ChevronLeft className="w-3 h-3" />
                Retour aux détails du cours
              </Link>
              <h2 className="font-heading font-bold text-sm leading-snug mb-3">
                {course?.title || "Contenu du cours"}
              </h2>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                <span>{percentComplete}% complété</span>
                <span>
                  {Math.max(currentIndex, 0) + (totalLessons ? 1 : 0)}/{totalLessons} leçons
                </span>
              </div>
              <Progress value={percentComplete} className="h-1.5" />
            </div>

            {/* Modules pliables + leçons */}
            <div className="flex-1 overflow-y-auto py-2">
              {lessons.length === 0 && (
                <p className="text-sm text-muted-foreground px-4 py-6">
                  Aucune leçon dans ce cours.
                </p>
              )}
              {moduleGroups.map((group, gIdx) => {
                const collapsed = collapsedModules[group.title];
                const moduleDone = group.lessons.filter(
                  (l) => currentIndex >= 0 && l.globalIndex < currentIndex
                ).length;
                return (
                  <div key={group.title} className="mb-1">
                    {/* En-tête de module */}
                    <button
                      onClick={() => toggleModule(group.title)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
                    >
                      <ChevronDown
                        className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${
                          collapsed ? "-rotate-90" : ""
                        }`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold truncate">
                          {gIdx + 1}. {group.title}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {moduleDone}/{group.lessons.length} leçons
                        </span>
                      </span>
                    </button>

                    {/* Leçons du module */}
                    {!collapsed &&
                      group.lessons.map((l) => {
                        const isCurrent = l.lesson_id === lessonId;
                        const unlocked = l.is_free || hasAccess;
                        const isDone = currentIndex >= 0 && l.globalIndex < currentIndex;
                        return (
                          <button
                            key={l.lesson_id}
                            onClick={() => openLesson(l)}
                            className={`w-full text-left flex items-start gap-3 pl-8 pr-4 py-2.5 transition-colors ${
                              isCurrent
                                ? "bg-primary/10 border-l-2 border-primary"
                                : "border-l-2 border-transparent hover:bg-muted/50"
                            } ${!unlocked ? "opacity-60" : ""}`}
                            data-testid={`outline-lesson-${l.lesson_id}`}
                          >
                            <span className="mt-0.5 shrink-0">
                              {!unlocked ? (
                                <Lock className="w-4 h-4 text-muted-foreground" />
                              ) : isDone ? (
                                <CheckCircle className="w-4 h-4 text-primary" />
                              ) : isCurrent ? (
                                <PlayCircle className="w-4 h-4 text-primary" />
                              ) : (
                                <Play className="w-4 h-4 text-muted-foreground" />
                              )}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span
                                className={`block text-sm truncate ${
                                  isCurrent ? "text-primary font-medium" : ""
                                }`}
                              >
                                {l.globalIndex + 1}. {l.title}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                <Clock className="w-3 h-3" />
                                {fmtDuration(l.duration_minutes)}
                                {l.is_free && (
                                  <span className="ml-1 text-primary">• Gratuit</span>
                                )}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </aside>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-border bg-card">
            <div className="flex gap-1 p-2">
              <Button
                variant={activeTab === "video" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("video")}
                data-testid="tab-video"
              >
                <Play className="w-4 h-4 mr-1" />
                Vidéo
              </Button>
              {lesson.lab && (
                <Button
                  variant={activeTab === "lab" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("lab")}
                  data-testid="tab-lab"
                >
                  <Terminal className="w-4 h-4 mr-1" />
                  Lab
                </Button>
              )}
              {lesson.quiz && (
                <Button
                  variant={activeTab === "quiz" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("quiz")}
                  data-testid="tab-quiz"
                >
                  <BookOpen className="w-4 h-4 mr-1" />
                  Quiz
                </Button>
              )}
              <Button
                variant={activeTab === "comments" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("comments")}
                data-testid="tab-comments"
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Discussion
              </Button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Video Tab */}
            {activeTab === "video" && (
              <div className="h-full flex flex-col">
                {/* Vidéo + progression : épinglées en haut */}
                <div className="p-4 shrink-0 border-b border-border">
                <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                  {(() => {
                    const url = lesson.video_url || "";
                    const ytId = getYouTubeId(url);
                    if (ytId) {
                      return (
                        <iframe
                          className="w-full h-full"
                          src={`https://www.youtube.com/embed/${ytId}?modestbranding=1&rel=0`}
                          title={lesson.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      );
                    }
                    const vimeoId = getVimeoId(url);
                    if (vimeoId) {
                      return (
                        <iframe
                          className="w-full h-full"
                          src={`https://player.vimeo.com/video/${vimeoId}`}
                          title={lesson.title}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      );
                    }
                    const driveId = getDriveId(url);
                    if (driveId) {
                      return (
                        <iframe
                          className="w-full h-full"
                          src={`https://drive.google.com/file/d/${driveId}/preview`}
                          title={lesson.title}
                          frameBorder="0"
                          allow="autoplay; fullscreen"
                          allowFullScreen
                        />
                      );
                    }
                    if (url) {
                      return (
                        <video
                          className="w-full h-full"
                          src={url}
                          controls
                          onTimeUpdate={handleVideoTimeUpdate}
                        />
                      );
                    }
                    return (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        Aucune vidéo disponible pour cette leçon
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-4">
                  <Progress value={progress} className="flex-1" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {Math.round(progress)}% complété
                  </span>
                  <Button size="sm" variant="outline" onClick={markComplete}>
                    Marquer comme terminé
                  </Button>
                </div>
                </div>
                {/* Titre + description : défilent sous la vidéo figée */}
                <div className="flex-1 overflow-y-auto p-4">
                  <h2 className="text-xl font-heading font-bold mb-2">{lesson.title}</h2>
                  <p className="text-muted-foreground whitespace-pre-line">{lesson.description}</p>
                </div>
              </div>
            )}

            {/* Lab Tab */}
            {activeTab === "lab" && lesson.lab && (
              <div className="p-4">
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-primary/20 text-primary border-primary/30">
                      {lesson.lab.environment.toUpperCase()}
                    </Badge>
                    <Badge variant="outline">
                      {lesson.lab.difficulty}
                    </Badge>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      ~{lesson.lab.estimated_time_minutes} min
                    </span>
                  </div>
                  <h2 className="text-xl font-heading font-bold mb-2">{lesson.lab.title}</h2>
                  <p className="text-muted-foreground mb-4">{lesson.lab.description}</p>
                  <div className="bg-muted/50 rounded-lg p-4 mb-4">
                    <h3 className="font-bold mb-2">Instructions:</h3>
                    <pre className="whitespace-pre-wrap text-sm font-mono">
                      {lesson.lab.instructions}
                    </pre>
                  </div>
                </div>
                <InteractiveTerminal 
                  labId={lesson.lab.lab_id} 
                  environment={lesson.lab.environment}
                />
              </div>
            )}

            {/* Quiz Tab */}
            {activeTab === "quiz" && lesson.quiz && (
              <div className="p-4 max-w-2xl">
                <h2 className="text-xl font-heading font-bold mb-4">{lesson.quiz.title}</h2>
                
                {quizResult ? (
                  <div className="space-y-4">
                    <div className={`p-6 rounded-lg border ${
                      quizResult.passed 
                        ? "bg-primary/10 border-primary/30" 
                        : "bg-destructive/10 border-destructive/30"
                    }`}>
                      <div className="flex items-center gap-3 mb-2">
                        {quizResult.passed ? (
                          <CheckCircle className="w-8 h-8 text-primary" />
                        ) : (
                          <Award className="w-8 h-8 text-destructive" />
                        )}
                        <div>
                          <h3 className="text-2xl font-heading font-bold">
                            {quizResult.score}%
                          </h3>
                          <p className="text-muted-foreground">
                            {quizResult.passed ? "Félicitations!" : "Essayez encore"}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {quizResult.results.map((result, idx) => (
                      <div key={idx} className="bg-card border border-border rounded-lg p-4">
                        <p className="font-medium mb-2">{result.question}</p>
                        <p className={result.is_correct ? "text-primary" : "text-destructive"}>
                          {result.is_correct ? "✓ Correct" : "✗ Incorrect"}
                        </p>
                        {result.explanation && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {result.explanation}
                          </p>
                        )}
                      </div>
                    ))}
                    
                    <Button onClick={() => { setQuizResult(null); setQuizAnswers(new Array(lesson.quiz.questions.length).fill(null)); }}>
                      Refaire le quiz
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {lesson.quiz.questions.map((q, qIdx) => (
                      <div key={qIdx} className="bg-card border border-border rounded-lg p-4">
                        <p className="font-medium mb-3">
                          {qIdx + 1}. {q.question}
                        </p>
                        <div className="space-y-2">
                          {q.options.map((option, oIdx) => (
                            <label
                              key={oIdx}
                              className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-colors ${
                                quizAnswers[qIdx] === oIdx
                                  ? "bg-primary/20 border-primary"
                                  : "bg-muted/50 border-border hover:border-primary/50"
                              }`}
                            >
                              <input
                                type="radio"
                                name={`question-${qIdx}`}
                                checked={quizAnswers[qIdx] === oIdx}
                                onChange={() => {
                                  const newAnswers = [...quizAnswers];
                                  newAnswers[qIdx] = oIdx;
                                  setQuizAnswers(newAnswers);
                                }}
                                className="hidden"
                              />
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                quizAnswers[qIdx] === oIdx ? "border-primary" : "border-muted-foreground"
                              }`}>
                                {quizAnswers[qIdx] === oIdx && (
                                  <div className="w-2 h-2 rounded-full bg-primary" />
                                )}
                              </div>
                              <span>{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    <Button onClick={submitQuiz} className="glow-green">
                      Soumettre le quiz
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Comments Tab */}
            {activeTab === "comments" && (
              <div className="p-4 max-w-2xl">
                <h2 className="text-xl font-heading font-bold mb-4">Discussion</h2>
                
                {/* New Comment */}
                <div className="mb-6">
                  <Textarea
                    placeholder="Posez une question ou partagez vos remarques..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="mb-2"
                    rows={3}
                    data-testid="comment-input"
                  />
                  <Button
                    onClick={submitComment}
                    disabled={!newComment.trim() || submittingComment}
                    data-testid="submit-comment-btn"
                  >
                    {submittingComment ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Publier
                  </Button>
                </div>

                {/* Comments List */}
                <div className="space-y-4">
                  {lesson.comments?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      Soyez le premier à commenter!
                    </p>
                  ) : (
                    lesson.comments?.map((comment) => (
                      <div key={comment.comment_id} className="bg-card border border-border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {comment.user_name?.charAt(0) || "U"}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{comment.user_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString("fr-FR")}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      <AIChatbot />
    </div>
  );
};

export default LessonPlayer;
